import { OctTable } from "@oceantech/oceantech-ui";
import React, {useCallback, useContext, useMemo, useState} from "react";
import { Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import AutocompleteV2 from "../../component/AutocompleteObjectV2";
import TextValidator from "../../component/TextValidator";
import {
  CODE_SUCCESS,
  ERROR_MESSAGE,
  MESSAGE,
  textSystem,
} from "../../utils/Constant";
import {
  EVALUATE_STATUS,
  RESULT,
  TYPE_MODAL,
} from "../constants/criteriaModal";
import { saveCriteria } from "../services/CriteriaServices";

import { AppContext } from "../../appContext/AppContext";
import { IFile, IViewFile } from "../../models/models";
import {
  handleDownloadFileUtil,
  handleUploadFileUtil,
  handleViewFileUtil,
} from "../../utils/FileUtils";
import ViewFile from "../modals/ViewFile";
import { FileActions } from "./FileActions";
import {ICriteriaModalProps} from "../models/CriteriaModels";
import {useCriteriaReportContext} from "../context/CriteriaReportContext";
import {CriteriaModalColumns} from "../columns";
import {
  divideCalculation,
  multiplicationCalculation,
  plusCalculation,
  resultCalculationFnc
} from "../helpers/qualityIndicator";
import {LIST_CRITERIA} from "../constants/commons";

const CriteriaTable = (props: ICriteriaModalProps) => {
  const {
    dataTable,
    setDataTable,
    objectSearch,
    setObjectSearch,
    configTable,
    handleDoubleClick,
    type,
    tableCache,
    setTableCache,
    reload,
    setReload,
  } = props;
  const {
    _83_TIEU_CHI,
    _CHI_SO_CHAT_LUONG,
    _TIEU_CHUAN_CHAT_LUONG,
    _XEP_CAP_CHUYEN_MON_KT,
    _MUC_DO_ATPT
  } = LIST_CRITERIA;
  const { setIsLoading } = useContext(AppContext);
  const {criteriaType, criteria} = useCriteriaReportContext();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [viewFile, setViewFile] = useState<IViewFile>({
    open: false,
    file: null,
  });

  const isSurgicalSafety = useMemo(() => criteriaType.value === _MUC_DO_ATPT.value, [criteriaType.value]);

  const validateFields = () => {
    const newErrors: { [key: string]: string } = {};
    let valid = true;

    dataTable?.forEach((row: any, index: number) => {
      if (!row.pointSelfAssessment) {
        newErrors[`pointSelfAssessment_${index}`] = MESSAGE.REQUIRED;
        valid = false;
      }

      if (!row.evidence) {
        newErrors[`evidence_${index}`] = MESSAGE.REQUIRED;
        valid = false;
      }

      if (!row.conclusionAssessment && type.modalType === TYPE_MODAL.EVALUATE) {
        newErrors[`conclusionAssessment_${index}`] = MESSAGE.REQUIRED;
        valid = false;
      }
    });

    setErrors(newErrors);
    return valid;
  };

  const handleViewFile = useCallback(async (file: IFile) => {
    const viewFile = await handleViewFileUtil(file, setIsLoading);
    if (viewFile) {
      setViewFile(viewFile);
    }
  }, []);

  const handleDownloadFile = useCallback(async (file: IFile) => {
    await handleDownloadFileUtil(file, setIsLoading);
  }, []);

  const handleChangeTable = useCallback(async (
    index: number,
    value: any,
    field: string,
    row: any
  ) => {
    const newDataTable = [...dataTable];

    if (field === "evidence") {
      newDataTable[index][field] = await handleUploadFileUtil(
        value,
        setIsLoading
      );
    } else if (field === "status") {
      newDataTable[index][field] = value
        ? EVALUATE_STATUS.EVALUATED.code
        : EVALUATE_STATUS.NOT_RATE_YET.code;
    } else if (field === "pointSelfAssessment" && isSurgicalSafety) {
      console.log(value, "value");
      dataTable[index][field] = value;
      dataTable[index].passingScore = multiplicationCalculation(
        dataTable[index]?.benchmark,
        value?.multiplier ?? 1,
      );
    } else if (["numerator", "denominator"].includes(field)) {
      newDataTable[index][field] = value;
      newDataTable[index]["result"] = divideCalculation(
        newDataTable[index]?.numerator,
        newDataTable[index]?.denominator
      );
    } else if (
      [
        "medicalUseOfAE",
        "adverseEventADR",
        "relatedToSurgeryAE",
        "medicalProcedureAE",
        "bloodTransfusionAE",
        "othersAE",
      ].includes(field)
    ) {
      newDataTable[index][field] = value;
      newDataTable[index]["result"] = plusCalculation([
        newDataTable[index]?.medicalUseOfAE || 0,
        newDataTable[index]?.adverseEventADR || 0,
        newDataTable[index]?.relatedToSurgeryAE || 0,
        newDataTable[index]?.medicalProcedureAE || 0,
        newDataTable[index]?.bloodTransfusionAE || 0,
        newDataTable[index]?.othersAE || 0,
      ]);
    } else if (
      [
        "suicideNCI",
        "heightFallingNCI",
        "childAbductionNCI",
        "violenceRelatedNCI",
        "explosionNCI",
        "hazardousMaterialLossNCI",
        "othersNCI",
      ].includes(field)
    ) {
      newDataTable[index][field] = value;
      newDataTable[index]["result"] = plusCalculation([
        newDataTable[index]?.suicideNCI || 0,
        newDataTable[index]?.heightFallingNCI || 0,
        newDataTable[index]?.childAbductionNCI || 0,
        newDataTable[index]?.violenceRelatedNCI || 0,
        newDataTable[index]?.explosionNCI || 0,
        newDataTable[index]?.hazardousMaterialLossNCI || 0,
        newDataTable[index]?.othersNCI || 0,
      ]);
    } else {
      newDataTable[index][field] = value;
    }

    setDataTable(newDataTable);
    const key = objectSearch?.level;
    setTableCache((prev: any) => ({
      ...prev,
      [key]: newDataTable,
    }));

    setTouched((prev) => ({
      ...prev,
      [`${field}_${index}`]: true,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTable, objectSearch?.level, setDataTable, setTableCache]);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const flattenData = Object.values(tableCache)?.flatMap((item: any) => item);
    const dataRequest = dataTable?.map((item: any) => {
      return {
        id: item?.id,
        criteriaSubsectionId: item?.criteriaSubsectionId,
        planCriteriaId: objectSearch?.planCriteriaId,
        fileId: item?.evidence?.id,
        fileName: item?.evidence?.name,
        pointSelfAssessment: item?.pointSelfAssessment?.id,
        conclusionAssessment: item?.conclusionAssessment?.id,
        comment: item?.comment,
        status: item?.status,
      };
    });
    setIsLoading(true);
    try {
      const { data } = await saveCriteria(dataRequest);
      if (data?.code === CODE_SUCCESS) {
        toast.success("Lưu thành công");
        setReload(!reload);
      }
    } catch (error) {
      console.error("error", error);
      toast.error(ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  const isFieldDisabled = () => {
    return type.modalType === TYPE_MODAL.VIEW || type.modalType === TYPE_MODAL.EVALUATE;
  };

  const columns = useMemo(() => CriteriaModalColumns(criteriaType?.value)({
    handleChangeTable,
    handleDoubleClick,
    type,
    isView: isFieldDisabled(),
    touched,
    errors,
    handleViewFile,
    handleDownloadFile,
    criteria,
  }), [
    criteriaType?.value,
    errors, 
    handleChangeTable,
    handleDoubleClick, 
    handleDownloadFile, 
    handleViewFile,
    isFieldDisabled,
    touched,
    type,
    criteria?.id,
  ]);

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <OctTable
          id="searchCriteria"
          className={`table-background-th spaces h-calc-vh-242`}
          data={dataTable}
          columns={columns}
          searchObject={objectSearch}
          setSearchObject={setObjectSearch}
          fixedColumnsCount={-1}
          noToolbar={true}
          totalPages={configTable.totalPages}
          totalElements={configTable.totalElements}
          numberOfElements={configTable.numberOfElements}
          isShowSelection={false}
          clearToolbar={true}
          noPagination={true}
        />

        {type.modalType !== TYPE_MODAL.VIEW && (
          <div className="position-fixed bottom-0 end-0 p-3 w-100 d-flex justify-content-end border-top">
            <Button className="btn-fill min-w-100px" type="submit">
              Lưu
            </Button>
          </div>
        )}
      </form>

      {viewFile?.open && (
        <ViewFile
          show={viewFile?.open}
          file={viewFile?.file}
          onHide={() => setViewFile({ open: false, file: null })}
        />
      )}
    </div>
  );
};

export default CriteriaTable;
